import React, { useState, useRef, useEffect } from 'react';
import { Container, Form, Button, Alert, Row, Col, Spinner, Card, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompactDisc, faDownload, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import api from '../api';

const POLL_INTERVAL_MS = 1500;

const FilmImportCecchiGori = () => {
    const [file, setFile] = useState(null);
    const [enableTmdb, setEnableTmdb] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const pollingRef = useRef(false);

    useEffect(() => {
        return () => { pollingRef.current = false; };
    }, []);

    const handleFileChange = (e) => {
        const f = e.target.files[0];
        setFile(f || null);
        setResult(null);
        setError('');
        setProgress(null);
    };

    const pollJob = async (jobId) => {
        pollingRef.current = true;
        while (pollingRef.current) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            if (!pollingRef.current) return;
            try {
                const resp = await api.get(`/admin/cecchi-gori/import-progress/${jobId}`);
                const { state, stats, report_url, report_filename, error: jobError } = resp.data;
                setProgress(stats);
                if (state === 'completed') {
                    setResult({
                        statistics: stats,
                        report_url,
                        report_filename,
                    });
                    pollingRef.current = false;
                    setUploading(false);
                    return;
                }
                if (state === 'error') {
                    setError(jobError || 'Errore durante l\'importazione');
                    pollingRef.current = false;
                    setUploading(false);
                    return;
                }
            } catch (err) {
                setError(`Errore polling: ${err.response?.data?.detail || err.message}`);
                pollingRef.current = false;
                setUploading(false);
                return;
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return;

        setUploading(true);
        setError('');
        setResult(null);
        setProgress({ totale: 0, creati: 0, saltati: 0, errori: 0 });

        const formData = new FormData();
        formData.append('file', file);

        try {
            const startResp = await api.post(
                `/admin/cecchi-gori/import-catalog?enable_tmdb=${enableTmdb}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const jobId = startResp.data.job_id;
            if (!jobId) {
                throw new Error('Risposta del server non valida (job_id mancante)');
            }
            pollJob(jobId);
        } catch (err) {
            const detail = err.response?.data?.detail || err.message;
            setError(`Errore: ${detail}`);
            setUploading(false);
        }
    };

    const handleDownloadReport = async () => {
        if (!result?.report_url) return;
        try {
            const resp = await api.get(result.report_url.replace('/api', ''), {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([resp.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', result.report_filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(`Errore download report: ${err.message}`);
        }
    };

    const processati = progress
        ? (progress.creati || 0) + (progress.saltati || 0) + (progress.errori || 0)
        : 0;
    const totale = progress?.totale || 0;
    const percent = totale > 0 ? Math.round((processati / totale) * 100) : 0;

    return (
        <Container className="py-4">
            <h2>
                <FontAwesomeIcon icon={faCompactDisc} className="me-2 text-primary" />
                Importa Catalogo Cecchi Gori (CG ENTERTAIMENT)
            </h2>
            <p className="text-muted">
                Carica un file XLSX con le colonne <code>ng | Cod.Art. | Descrizione Articolo | data entrata | PVP | Regia | Attori | Genere</code> (header sulla seconda riga del foglio).
                Consigliato max <strong>250 righe per batch</strong>. I film con SKU <code>MC01_&lt;ng&gt;</code> già esistente vengono saltati e segnalati nel report.
            </p>

            <Card className="mt-3">
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group as={Row} className="mb-3">
                            <Form.Label column sm={3}>File XLSX</Form.Label>
                            <Col sm={9}>
                                <Form.Control
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    disabled={uploading}
                                    required
                                />
                                {file && <Form.Text className="text-success">✅ {file.name}</Form.Text>}
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3">
                            <Col sm={{ span: 9, offset: 3 }}>
                                <Form.Check
                                    type="checkbox"
                                    label="Arricchimento TMDB (anno, durata, trama, rating, poster di fallback)"
                                    checked={enableTmdb}
                                    onChange={(e) => setEnableTmdb(e.target.checked)}
                                    disabled={uploading}
                                />
                                <Form.Text className="text-muted">
                                    Con TMDB attivo l'import richiede ~2-5 secondi per film. Per un batch da 250 conta su 5-15 minuti.
                                </Form.Text>
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row}>
                            <Col sm={{ span: 9, offset: 3 }}>
                                <Button type="submit" variant="primary" disabled={!file || uploading}>
                                    {uploading ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                                            Importazione in corso…
                                        </>
                                    ) : (
                                        'Importa'
                                    )}
                                </Button>
                            </Col>
                        </Form.Group>
                    </Form>
                </Card.Body>
            </Card>

            {uploading && progress && (
                <Card className="mt-3">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Avanzamento</strong>
                            <span className="text-muted">
                                {totale > 0
                                    ? `${processati} / ${totale} righe (${percent}%)`
                                    : 'Parsing del file in corso…'}
                            </span>
                        </div>
                        <ProgressBar
                            now={totale > 0 ? percent : 100}
                            animated={totale === 0}
                            striped
                            label={totale > 0 ? `${percent}%` : ''}
                        />
                        <Row className="mt-3 text-center">
                            <Col><div className="text-muted small">Creati</div><div className="fs-5 text-success">{progress.creati || 0}</div></Col>
                            <Col><div className="text-muted small">Saltati</div><div className="fs-5">{progress.saltati || 0}</div></Col>
                            <Col><div className="text-muted small">Avvisi</div><div className={`fs-5 ${(progress.avvisi || 0) > 0 ? 'text-warning' : ''}`}>{progress.avvisi || 0}</div></Col>
                            <Col><div className="text-muted small">Errori</div><div className={`fs-5 ${(progress.errori || 0) > 0 ? 'text-danger' : ''}`}>{progress.errori || 0}</div></Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {error && (
                <Alert variant="danger" className="mt-3">{error}</Alert>
            )}

            {result && (
                <Card className="mt-3">
                    <Card.Header>
                        <FontAwesomeIcon icon={faFileExcel} className="me-2 text-success" />
                        Esito importazione
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col sm={3}><strong>Totale righe:</strong></Col>
                            <Col sm={9}>{result.statistics?.totale ?? 0}</Col>
                        </Row>
                        <Row>
                            <Col sm={3}><strong>✅ Creati:</strong></Col>
                            <Col sm={9}>{result.statistics?.creati ?? 0}</Col>
                        </Row>
                        <Row>
                            <Col sm={3}><strong>⏭️ Saltati (SKU esistenti):</strong></Col>
                            <Col sm={9}>{result.statistics?.saltati ?? 0}</Col>
                        </Row>
                        <Row>
                            <Col sm={3}><strong>⚠️ Avvisi (genere/TMDB/immagine):</strong></Col>
                            <Col sm={9} className={result.statistics?.avvisi > 0 ? 'text-warning' : ''}>
                                {result.statistics?.avvisi ?? 0}
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={3}><strong>❌ Errori:</strong></Col>
                            <Col sm={9} className={result.statistics?.errori > 0 ? 'text-danger' : ''}>
                                {result.statistics?.errori ?? 0}
                            </Col>
                        </Row>
                        {result.report_url && (
                            <div className="mt-3">
                                <Button variant="outline-success" size="sm" onClick={handleDownloadReport}>
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    Scarica report XLSX
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default FilmImportCecchiGori;
